/**
 * The things every fetch module shares: the error type and the CSRF header. They live apart
 * from api.ts so that the auth client (auth/authApi.ts) and the auth state holder (lib/auth.ts)
 * can use them without importing api.ts, which imports the state holder for its 401 handling.
 */

/** Every mutating request carries this (the server's `checkMutatingRequest`). */
export const MUTATING_HEADER = { 'x-market-analysis': '1' } as const;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** The `{error}` text of the body, when the server sent one. Null for a bare status. */
    public serverMessage: string | null = null,
  ) {
    super(message);
  }
}

/** The `{error}` string of a JSON body, or null when the body is anything else. */
function errorField(body: string): string | null {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      const { error } = parsed as { error: unknown };
      if (typeof error === 'string' && error) return error;
    }
  } catch {
    // not JSON
  }
  return null;
}

/**
 * An ApiError for a refused response. The message is the server's `{error}` text when the body
 * has one, else the raw body, else the status text. `serverMessage` keeps the server's own words
 * apart from those fallbacks, so a view can choose its own copy when the server said nothing.
 */
export async function apiErrorFrom(res: Response): Promise<ApiError> {
  const body = await res.text().catch(() => '');
  const fromServer = errorField(body);
  return new ApiError(res.status, fromServer ?? (body || res.statusText), fromServer);
}

/**
 * The server writes its errors as lower-case clauses ("the current password is wrong"). A notice
 * is a sentence, so it gets a capital and a full stop. Nothing else about the text changes.
 */
export function asSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const capital = trimmed[0]!.toUpperCase() + trimmed.slice(1);
  return /[.!?…]$/.test(capital) ? capital : `${capital}.`;
}
